import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Divider,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  CalendarToday,
  Info,
  CheckCircle,
  Cancel,
  EventAvailable,
  FilterList,
  Refresh
} from '@mui/icons-material';
import Sidebar from '../components/Sidebar';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO } from 'date-fns';
import api from '../services/api';
import AccessTime from '@mui/icons-material/AccessTime';
import People from '@mui/icons-material/People';
import TableRestaurant from '@mui/icons-material/TableRestaurant';
import Chair from '@mui/icons-material/Chair';
import Notes from '@mui/icons-material/Notes';
import moment from 'moment';
// FullCalendar imports with custom styling
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import './calendarStyles.css'; // Custom CSS for calendar

const statusOptions = [
  { value: 'all', label: 'All Status', icon: <FilterList fontSize="small" /> },
  { value: 'pending', label: 'Pending', icon: <Info fontSize="small" /> },
  { value: 'confirmed', label: 'Confirmed', icon: <CheckCircle fontSize="small" /> },
  { value: 'cancelled', label: 'Cancelled', icon: <Cancel fontSize="small" /> },
  { value: 'completed', label: 'Completed', icon: <EventAvailable fontSize="small" /> },
];

const CalendarView = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingDialog, setBookingDialog] = useState({ open: false, mode: 'view' });
  const [filterStatus, setFilterStatus] = useState('all');
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    cancelled: 0,
    completed: 0
  });

  useEffect(() => {
    fetchBookings();
  }, [selectedDate, filterStatus]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/bookings', {
        params: {
          date: format(selectedDate, 'yyyy-MM-dd'),
          status: filterStatus === 'all' ? undefined : filterStatus,
        },
      });
      response.data.bookings=response.data
      
      console.log(response.data,"response.dataresponse.data")
      setBookings(response.data.bookings);
      setStats(response.data.stats || {
        total: response.data.bookings.length,
        pending: response.data.bookings.filter(b => b.status === 'pending').length,
        confirmed: response.data.bookings.filter(b => b.status === 'confirmed').length,
        cancelled: response.data.bookings.filter(b => b.status === 'cancelled').length,
        completed: response.data.bookings.filter(b => b.status === 'completed').length
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleBookingClick = (info) => {
    setSelectedBooking(info.event.extendedProps);
    setBookingDialog({ open: true, mode: 'view' });
  };

  const handleUpdateStatus = async (bookingId, status) => {
    try {
      await api.patch(`/bookings/${bookingId}/status`, { status });
      fetchBookings();
      setBookingDialog({ open: false, mode: 'view' });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update booking status');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'confirmed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'completed':
        return 'info';
      default:
        return 'default';
    }
  };

  
  const renderEventContent = (eventInfo) => {
    return (
      <Box sx={{ p: 0.5 }}>
        <b>{eventInfo.timeText}</b>
        <Divider sx={{ my: 0.5 }} />
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar 
            sx={{ 
              width: 24, 
              height: 24, 
              fontSize: '0.75rem',
              mr: 1,
              bgcolor: getStatusColor(eventInfo.event.extendedProps.status) + '.light'
            }}
          >
            {eventInfo.event.extendedProps.user.name.charAt(0)}
          </Avatar>
          <span>{eventInfo.event.title}</span>
        </Box>
      </Box>
    );
  };

  if (loading && bookings.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>Loading calendar data...</Typography>
      </Container>
    );
  }
const DetailItem = ({ label, value, icon, small = false }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      border: "1px solid",
      borderColor: "divider", // uses theme divider color
      borderRadius: 1, // rounded corners
      p: 1, // padding inside
      mb: 1, // spacing between rows
      bgcolor: "background.paper" // matches theme
    }}
  >
    {icon && (
      <Box sx={{ color: "text.secondary" }}>
        {icon}
      </Box>
    )}
    <Typography
      variant={small ? "caption" : "subtitle2"}
      color="text.secondary"
      sx={{ minWidth: 130 }}
    >
      {label}:
    </Typography>
    <Typography
      variant={small ? "body2" : "body1"}
      fontWeight={small ? 400 : 500}
    >
      {value}
    </Typography>
  </Box>
);


  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ display: 'flex' }}>
        <Sidebar />
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          {/* Header Section */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                {/* <CalendarToday sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} /> */}
               Table Booking Calendar
              </Typography>
     
            </Box>

            <Box display="flex" gap={2} alignItems="center">
              <Tooltip title="Refresh data">
                <IconButton onClick={fetchBookings} color="primary">
                  <Refresh />
                </IconButton>
              </Tooltip>
              
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Filter Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Filter Status"
                  onChange={(e) => setFilterStatus(e.target.value)}
                  startAdornment={
                    statusOptions.find(opt => opt.value === filterStatus)?.icon
                  }
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {/* {option.icon} */}
                        {option.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <DatePicker
                label="Select Date"
                value={selectedDate}
                onChange={handleDateChange}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    size="small" 
                    sx={{ width: 180 }}
                  />
                )}
              />
            </Box>
          </Box>

          {/* Stats Cards */}
          <Box display="flex" gap={2} mb={3} flexWrap="wrap">
            <StatCard 
              value={stats.total}
              label="Total Bookings"
              color="#4e73df"
              icon={<CalendarToday />}
            />
            <StatCard 
              value={stats.pending}
              label="Pending"
              color="#f6c23e"
              icon={<Info />}
            />
            <StatCard 
              value={stats.confirmed}
              label="Confirmed"
              color="#1cc88a"
              icon={<CheckCircle />}
            />
            <StatCard 
              value={stats.cancelled}
              label="Cancelled"
              color="#e74a3b"
              icon={<Cancel />}
            />
            <StatCard 
              value={stats.completed}
              label="Completed"
              color="#36b9cc"
              icon={<EventAvailable />}
            />
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 2 }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}

          {/* Calendar Section */}
          <Paper elevation={3} sx={{ p: 2, borderRadius: 3 }}>
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
              initialView="dayGridMonth"
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
              }}
             events={Array.isArray(bookings) ? bookings.map((booking) => {
  // Check if booking exists and has required properties
  if (!booking || !booking._id || !booking.user || !booking.table || !booking.date || !booking.time) {
    console.warn('Invalid booking data:', booking);
    return null;
  }

  return {
    id: booking._id,
    title: `${booking.user.name || 'Guest'} - ${booking.table.tableNumber || 'N/A'}`,
    start: `${booking.date.split('T')[0]}T${booking.time}`,
    extendedProps: booking,
    backgroundColor: getEventColor(booking.status),
    borderColor: getEventColor(booking.status),
    textColor: '#fff',
  };
}).filter(Boolean) : []}
              eventContent={renderEventContent}
              eventClick={handleBookingClick}
              height="auto"
              nowIndicator
              editable={false}
              selectable={false}
              initialDate={selectedDate}
              dayMaxEvents={3}
              eventDisplay="block"
              eventTimeFormat={{
    hour: 'numeric',
    minute: '2-digit',
    meridiem: 'short'
  }}
  slotLabelFormat={{
    hour: 'numeric',
    minute: '2-digit',
    meridiem: 'short'
  }}

              views={{
                dayGridMonth: { buttonText: 'Month' },
                timeGridWeek: { buttonText: 'Week' },
                timeGridDay: { buttonText: 'Day' },
                listWeek: { buttonText: 'List' }
              }}
            />
          </Paper>

          {/* Booking Details Dialog */}
       {/* Booking Details Dialog - Enhanced Design */}
<Dialog
  open={bookingDialog.open}
  onClose={() => setBookingDialog({ open: false, mode: 'view' })}
  maxWidth="sm"
  fullWidth
>
  {selectedBooking && (
    <>
      <DialogTitle sx={{ 
        bgcolor: 'primary.main',
        color: 'white',
        py: 2
      }}>
        <Typography variant="h6">Booking Details</Typography>
        <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>
          {format(parseISO(selectedBooking.date), 'EEEE, MMMM do yyyy')}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
    <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            {/* Customer Info */}
            <DetailItem label="Customer Name" value={selectedBooking.user?.name || 'Guest'} />
            <DetailItem label="Contact Phone" value={selectedBooking.user?.phone || 'Not provided'} />
            
            {/* Booking Details */}
            <DetailItem label="Reservation Time"   value={moment(selectedBooking.time, "HH:mm").format("hh:mm A")} />
            <DetailItem label="Party Size" value={`${selectedBooking.numberOfGuests} people`} />
            <DetailItem label="Table Number" value={selectedBooking.table?.tableNumber || 'Not assigned'} />
            <DetailItem label="Table Capacity" value={selectedBooking.table?.capacity ? `${selectedBooking.table.capacity} seats` : 'N/A'} />
            <DetailItem label="Status" value={
              <Chip 
                label={selectedBooking.status} 
                size="small" 
                sx={{ 
                  backgroundColor: `${getStatusColor(selectedBooking.status)}.light`,
                  color: `${getStatusColor(selectedBooking.status)}.dark`,
                  fontWeight: 'bold'
                }}
              />
            } />

            {/* Special Requests */}
            {selectedBooking.specialRequests && (
              <DetailItem 
                label="Special Requests" 
                value={selectedBooking.specialRequests} 
                multiline 
              />
            )}

            {/* Metadata */}
            <DetailItem label="Created On" value={format(parseISO(selectedBooking.createdAt), 'MMM d, yyyy h:mm a')} small />
            <DetailItem label="Last Updated" value={format(parseISO(selectedBooking.updatedAt), 'MMM d, yyyy h:mm a')} small />
          </tbody>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button 
          onClick={() => setBookingDialog({ open: false, mode: 'view' })}
          variant="outlined"
        >
          Close
        </Button>
        
        {selectedBooking.status === 'pending' && (
          <>
            <Button
              variant="contained"
              color="success"
              onClick={() => handleUpdateStatus(selectedBooking._id, 'confirmed')}
            >
              Confirm Booking
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => handleUpdateStatus(selectedBooking._id, 'cancelled')}
            >
              Cancel Booking
            </Button>
          </>
        )}
      </DialogActions>
    </>
  )}
</Dialog>


        </Box>
      </Box>
    </LocalizationProvider>
  );
};

// Helper components
const StatCard = ({ value, label, color, icon }) => (
  <Paper 
    elevation={2} 
    sx={{ 
      p: 2, 
      flex: 1, 
      minWidth: 180, 
      borderRadius: 2,
      borderLeft: `4px solid ${color}`
    }}
  >
    <Box display="flex" justifyContent="space-between">
      <Box>
        <Typography variant="h5" fontWeight="bold">{value}</Typography>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
      </Box>
      <Avatar sx={{ bgcolor: color + '20', color }}>
        {React.cloneElement(icon, { fontSize: 'small' })}
      </Avatar>
    </Box>
  </Paper>
);

const DetailItem = ({ label, value, multiline = false }) => (
  <Box>
    <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
    {multiline ? (
      <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>{value}</Typography>
    ) : (
      <Typography variant="body1" fontWeight="500">{value}</Typography>
    )}
  </Box>
);

const getEventColor = (status) => {
  switch(status) {
    case 'pending': return '#f6c23e';
    case 'confirmed': return '#1cc88a';
    case 'cancelled': return '#e74a3b';
    case 'completed': return '#36b9cc';
    default: return '#4e73df';
  }
};

export default CalendarView;